import { useId, useState } from 'react'
import { Alert, Button, Loader, Popover, SegmentedControl, Select, Text } from '@mantine/core'
import { DatePicker, type DatesRangeValue } from '@mantine/dates'
import {
  POPULATION_MAX_CUSTOM_DAYS,
  POPULATION_PERIODS,
  type PopulationComparison,
  type PopulationPeriod,
} from '@behuman/core-contract'
import { formatDateRange, formatDay, formatInstant } from '../format'
import type { PopulationQuery, PopulationWindow } from '../hooks'
import { COMPARISON_LABELS, PERIOD_LABELS, options } from '../labels'
import type { ParamKey } from '../usePopulationParams'
import { rangeDayCount, utcToday } from '../utils'

type DayRange = { start: string; end: string }

/** The first of the month before `day`'s, so the two-month calendar ends on `day`'s month. */
const monthBefore = (day: string) => {
  const [y, m] = day.split('-').map(Number)
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`
}

/**
 * The custom period's two-month range calendar. Days after today (UTC — the API's "today")
 * are out, and once a start is picked, so is anything that would make the range longer than
 * POPULATION_MAX_CUSTOM_DAYS. Picking the second day applies the range.
 */
function CustomRangeCalendar({
  range,
  onApply,
}: {
  range: DayRange | undefined
  onApply: (range: DayRange) => void
}) {
  const today = utcToday()
  const [value, setValue] = useState<DatesRangeValue<string>>(
    range ? [range.start, range.end] : [null, null],
  )
  const [first, second] = value
  return (
    <DatePicker
      type="range"
      numberOfColumns={2}
      allowSingleDateInRange
      defaultDate={monthBefore(range?.end ?? today)}
      maxDate={today}
      value={value}
      excludeDate={(day) =>
        first !== null &&
        second === null &&
        rangeDayCount(day < first ? day : first, day < first ? first : day) >
          POPULATION_MAX_CUSTOM_DAYS
      }
      onChange={(next) => {
        setValue(next)
        const [start, end] = next
        if (start && end) onApply({ start, end })
      }}
    />
  )
}

function HowChangesAreCalculated() {
  return (
    <Popover width={340} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <Button variant="subtle" size="compact-xs">
          How changes are calculated
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm" mb={6}>
          Every figure is compared with the same figure over the comparison dates shown here.
        </Text>
        <Text size="sm" mb={6}>
          Counts show the percentage change. Times to resolve show the change in days: a median time
          to resolve going from 40 to 46 days is up 6 days, which is worse.
        </Text>
        <Text size="sm">
          “—” means there is nothing to compare against, such as a count that was 0, or a time to
          resolve when nothing was resolved, in either period.
        </Text>
        <Text size="sm" mt={6}>
          The previous calendar year is compared with the calendar year before it, whichever
          comparison is chosen.
        </Text>
      </Popover.Dropdown>
    </Popover>
  )
}

/** "Data through … · refreshed …" from `window`, for the filter row's right side. */
export function Freshness({ window }: { window: PopulationWindow | undefined }) {
  if (!window) return null
  return (
    <span className="text-xs text-slate-500">
      {window.dataThrough ? `Data through ${formatDay(window.dataThrough)}` : 'No findings yet'}
      {' · '}
      {window.refreshedAt ? `refreshed ${formatInstant(window.refreshedAt)}` : 'not refreshed yet'}
    </span>
  )
}

/** The page title, the period and comparison pickers, and the exact dates they cover. */
export function PeriodBar({
  query,
  window,
  stale,
  setPeriod,
  setParam,
}: {
  query: PopulationQuery
  window: PopulationWindow | undefined
  /** `window` is still the previous selection's while the new one loads. */
  stale: boolean
  setPeriod: (period: PopulationPeriod, range?: DayRange) => void
  setParam: (key: ParamKey, value: string | null) => void
}) {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarTitleId = useId()
  const custom =
    query.period === 'custom' && query.start && query.end
      ? { start: query.start, end: query.end }
      : undefined
  const periodData = POPULATION_PERIODS.map((period) =>
    period === 'custom'
      ? {
          value: period,
          // Clicking the label reopens the calendar even when Custom is already selected
          // (the control fires no change then).
          label: (
            <span onClick={() => setCalendarOpen(true)}>
              {custom ? formatDateRange(custom) : PERIOD_LABELS.custom}
            </span>
          ),
        }
      : { value: period, label: PERIOD_LABELS[period] },
  )
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900">Population Health</h1>
          {window && (
            <div
              aria-busy={stale}
              className={`mt-1 flex flex-wrap items-center gap-x-2 text-sm text-slate-500 ${stale ? 'opacity-50' : ''}`}
            >
              {stale && <Loader size="xs" aria-label="Updating dates" />}
              <span>
                <span className="font-medium text-slate-800">
                  {formatDateRange(window.current)}
                </span>
                {' vs '}
                <span className="font-medium text-slate-800">
                  {formatDateRange(window.comparison)}
                </span>
              </span>
              <HowChangesAreCalculated />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Popover
            opened={calendarOpen}
            onChange={setCalendarOpen}
            position="bottom-end"
            shadow="md"
            withArrow
            trapFocus
          >
            <Popover.Target>
              {/* Six periods outgrow a phone's width: scroll rather than squash. Enter or Space
                  on the Custom radio reopens the calendar for the keyboard, as a click does. */}
              <div
                className="max-w-full overflow-x-auto"
                onKeyDown={(event) => {
                  const target = event.target as HTMLInputElement
                  if (target.value === 'custom' && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    setCalendarOpen(true)
                  }
                }}
              >
                <SegmentedControl
                  aria-label="Period"
                  radius="md"
                  value={query.period}
                  // Custom only opens the calendar; the period changes once a range is picked.
                  onChange={(value) =>
                    value === 'custom'
                      ? setCalendarOpen(true)
                      : setPeriod(value as PopulationPeriod)
                  }
                  data={periodData}
                />
              </div>
            </Popover.Target>
            {/* Named by its own title: Mantine would otherwise name it after the whole control. */}
            <Popover.Dropdown aria-labelledby={calendarTitleId}>
              <span id={calendarTitleId} className="sr-only">
                Custom period
              </span>
              <CustomRangeCalendar
                range={custom}
                onApply={(range) => {
                  setCalendarOpen(false)
                  setPeriod('custom', range)
                }}
              />
            </Popover.Dropdown>
          </Popover>
          <Select
            aria-label="Compare with"
            value={query.comparison}
            onChange={(value) => value && setParam('comparison', value)}
            data={options<PopulationComparison>(COMPARISON_LABELS)}
            allowDeselect={false}
            w={220}
          />
        </div>
      </div>
      {window && window.dataThrough === null && (
        <Alert color="yellow" variant="light" title="No findings history yet">
          The dashboard counts Risks &amp; Gaps findings. None have been recorded for these patients
          yet, so every figure below is empty until the first findings are built.
        </Alert>
      )}
    </div>
  )
}
